import { EXPRESSION_TYPES } from "./expressionTypes";
import { DATA_FIELD_TYPE } from "Types/dataFieldTypes";
import { deepClone } from "Util/util.js";

export const EXPRESSION_GROUP = {
    Numeric: { groupName: "Numeric", groupDisplay: "Numeric" },
    Relational: { groupName: "Relational", groupDisplay: "Relational" },
    Boolean: { groupName: "Boolean", groupDisplay: "Logical" },
    String: { groupName: "String", groupDisplay: "Text" },
    Date: { groupName: "Date", groupDisplay: "Date" },
    Special: { groupName: "Special", groupDisplay: "Dynamic" },
    MultiInstance: { groupName: "MultiInstance", groupDisplay: "Multi-Instance" }
};

export function getMetadata(exp) {
    const meta = EXPRESSION_TYPE_METADATA[exp.Type];
    if (!meta.isDynamic) {
        return meta;
    }
    if (!meta.dynamicGenerator) {
        if (
            (meta.group === EXPRESSION_GROUP.Special.groupName ||
                meta.group === EXPRESSION_GROUP.MultiInstance.groupName) &&
            exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.MetaResultType]
        ) {
            const tmpMeta = deepClone(meta);
            tmpMeta.resultType = parseInt(exp.PropertyDictionary[EXPRESSION_PROPERTY_NAMES.MetaResultType]);
            return tmpMeta;
        }
        return meta;
    }
    const numOperands = parseInt(exp.PropertyDictionary._meta_numoperands);
    return meta.dynamicGenerator({ numOperands }, meta.defaultProperties);
}

function variableNumberDecimalOperandMetadata(metaProperties, defaultProperties) {
    const nOperands =
        metaProperties.numOperands != null && !isNaN(metaProperties.numOperands)
            ? metaProperties.numOperands
            : defaultProperties.numOperands;

    const operandTypes = [];
    for (let i = 0; i < nOperands; i++) {
        operandTypes.push(DATA_FIELD_TYPE.Decimal);
    }
    return {
        numOperands: nOperands,
        operandTypes: operandTypes,
        resultType: DATA_FIELD_TYPE.Decimal,
        group: EXPRESSION_GROUP.Numeric.groupName
    };
}

const expressionTypeMetadata = {
    binaryNumeric: {
        numOperands: 2,
        operandTypes: [DATA_FIELD_TYPE.Decimal, DATA_FIELD_TYPE.Decimal],
        resultType: DATA_FIELD_TYPE.Decimal,
        group: EXPRESSION_GROUP.Numeric.groupName
    },
    leafNumeric: {
        numOperands: 0,
        operandTypes: null,
        resultType: DATA_FIELD_TYPE.Decimal,
        group: EXPRESSION_GROUP.Numeric.groupName
    },
    conditionalNumeric: {
        numOperands: 3,
        operandTypes: [DATA_FIELD_TYPE.Boolean, DATA_FIELD_TYPE.Decimal, DATA_FIELD_TYPE.Decimal],
        resultType: DATA_FIELD_TYPE.Decimal,
        group: EXPRESSION_GROUP.Numeric.groupName
    },
    unaryNumeric: {
        numOperands: 1,
        operandTypes: [DATA_FIELD_TYPE.Decimal],
        resultType: DATA_FIELD_TYPE.Decimal,
        group: EXPRESSION_GROUP.Numeric.groupName
    },
    variableNumeric(defaultProperties) {
        return {
            isDynamic: true,
            dynamicGenerator: variableNumberDecimalOperandMetadata,
            resultType: DATA_FIELD_TYPE.Decimal,
            group: EXPRESSION_GROUP.Numeric.groupName,
            defaultProperties
        };
    },
    binaryRelational: {
        numOperands: 2,
        operandTypes: [DATA_FIELD_TYPE.Decimal, DATA_FIELD_TYPE.Decimal],
        resultType: DATA_FIELD_TYPE.Boolean,
        group: EXPRESSION_GROUP.Relational.groupName
    },
    binaryStringRelational: {
        numOperands: 2,
        operandTypes: [DATA_FIELD_TYPE.String, DATA_FIELD_TYPE.String],
        resultType: DATA_FIELD_TYPE.Boolean,
        group: EXPRESSION_GROUP.Relational.groupName
    },
    binaryBoolean: {
        numOperands: 2,
        operandTypes: [DATA_FIELD_TYPE.Boolean, DATA_FIELD_TYPE.Boolean],
        resultType: DATA_FIELD_TYPE.Boolean,
        group: EXPRESSION_GROUP.Boolean.groupName
    },
    unaryBoolean: {
        numOperands: 1,
        operandTypes: [DATA_FIELD_TYPE.Boolean],
        resultType: DATA_FIELD_TYPE.Boolean,
        group: EXPRESSION_GROUP.Boolean.groupName
    },
    leafBoolean: {
        numOperands: 0,
        operandTypes: null,
        resultType: DATA_FIELD_TYPE.Boolean,
        group: EXPRESSION_GROUP.Boolean.groupName
    },
    leafChoiceComparison: {
        numOperands: 0,
        operandTypes: null,
        resultType: DATA_FIELD_TYPE.Boolean,
        group: EXPRESSION_GROUP.Boolean.groupName
    },
    leafString: {
        numOperands: 0,
        operandTypes: null,
        resultType: DATA_FIELD_TYPE.String,
        group: EXPRESSION_GROUP.String.groupName
    },
    conditionalString: {
        numOperands: 3,
        operandTypes: [DATA_FIELD_TYPE.Boolean, DATA_FIELD_TYPE.String, DATA_FIELD_TYPE.String],
        resultType: DATA_FIELD_TYPE.String,
        group: EXPRESSION_GROUP.String.groupName
    },
    conditionalDate: {
        numOperands: 3,
        operandTypes: [DATA_FIELD_TYPE.Boolean, DATA_FIELD_TYPE.DateTime, DATA_FIELD_TYPE.DateTime],
        resultType: DATA_FIELD_TYPE.DateTime,
        group: EXPRESSION_GROUP.Date.groupName
    },
    binaryDate: {
        numOperands: 2,
        operandTypes: [DATA_FIELD_TYPE.DateTime, DATA_FIELD_TYPE.DateTime],
        resultType: DATA_FIELD_TYPE.Boolean,
        group: EXPRESSION_GROUP.Relational.groupName
    },
    binaryDateNumeric: {
        numOperands: 2,
        operandTypes: [DATA_FIELD_TYPE.DateTime, DATA_FIELD_TYPE.DateTime],
        resultType: DATA_FIELD_TYPE.Decimal,
        group: EXPRESSION_GROUP.Numeric.groupName
    },
    leafDate: {
        numOperands: 0,
        operandTypes: null,
        resultType: DATA_FIELD_TYPE.DateTime,
        group: EXPRESSION_GROUP.Date.groupName
    },
    binaryNumericDate: {
        numOperands: 2,
        operandTypes: [DATA_FIELD_TYPE.Decimal, DATA_FIELD_TYPE.DateTime],
        resultType: DATA_FIELD_TYPE.DateTime,
        group: EXPRESSION_GROUP.Date.groupName
    },
    unaryStringNumeric: {
        numOperands: 1,
        operandTypes: [DATA_FIELD_TYPE.String],
        resultType: DATA_FIELD_TYPE.Decimal,
        group: EXPRESSION_GROUP.String.groupName
    },
    binaryString: {
        numOperands: 2,
        operandTypes: [DATA_FIELD_TYPE.String, DATA_FIELD_TYPE.String],
        resultType: DATA_FIELD_TYPE.String,
        group: EXPRESSION_GROUP.String.groupName
    },
    leafFormulaRef: {
        numOperands: 0,
        operandTypes: null,
        isDynamic: true,
        group: EXPRESSION_GROUP.Special.groupName
    },
    leafMultiInstanceBoolean: {
        numOperands: 0,
        operandTypes: null,
        resultType: DATA_FIELD_TYPE.Boolean,
        group: EXPRESSION_GROUP.MultiInstance.groupName
    },
    leafMultiInstanceDynamic: {
        numOperands: 0,
        operandTypes: null,
        isDynamic: true,
        group: EXPRESSION_GROUP.MultiInstance.groupName
    },
    leafMultiInstanceNumeric: {
        numOperands: 0,
        operandTypes: null,
        resultType: DATA_FIELD_TYPE.Decimal,
        group: EXPRESSION_GROUP.MultiInstance.groupName
    }
};
export const EXPRESSION_TYPE_METADATA = {
    [EXPRESSION_TYPES.Addition]: expressionTypeMetadata.binaryNumeric,
    [EXPRESSION_TYPES.Subtraction]: expressionTypeMetadata.binaryNumeric,
    [EXPRESSION_TYPES.Multiplication]: expressionTypeMetadata.binaryNumeric,
    [EXPRESSION_TYPES.Division]: expressionTypeMetadata.binaryNumeric,
    [EXPRESSION_TYPES.Constant]: expressionTypeMetadata.leafNumeric,
    [EXPRESSION_TYPES.DataFieldReference]: expressionTypeMetadata.leafNumeric,
    [EXPRESSION_TYPES.EqualTo]: expressionTypeMetadata.binaryRelational,
    [EXPRESSION_TYPES.NotEqualTo]: expressionTypeMetadata.binaryRelational,
    [EXPRESSION_TYPES.GreaterThan]: expressionTypeMetadata.binaryRelational,
    [EXPRESSION_TYPES.LessThan]: expressionTypeMetadata.binaryRelational,
    [EXPRESSION_TYPES.GreaterThanOrEqualTo]: expressionTypeMetadata.binaryRelational,
    [EXPRESSION_TYPES.LessThanOrEqualTo]: expressionTypeMetadata.binaryRelational,
    [EXPRESSION_TYPES.And]: expressionTypeMetadata.binaryBoolean,
    [EXPRESSION_TYPES.Or]: expressionTypeMetadata.binaryBoolean,
    [EXPRESSION_TYPES.Not]: expressionTypeMetadata.unaryBoolean,
    [EXPRESSION_TYPES.BooleanConstant]: expressionTypeMetadata.leafBoolean,
    [EXPRESSION_TYPES.BooleanDataFieldReference]: expressionTypeMetadata.leafBoolean,
    [EXPRESSION_TYPES.AttachmentIsApprovedReference]: expressionTypeMetadata.leafBoolean,
    [EXPRESSION_TYPES.AttachmentIsRejectedReference]: expressionTypeMetadata.leafBoolean,
    [EXPRESSION_TYPES.ConditionalNumeric]: expressionTypeMetadata.conditionalNumeric,
    [EXPRESSION_TYPES.StringEqual]: expressionTypeMetadata.binaryStringRelational,
    [EXPRESSION_TYPES.StringNotEqual]: expressionTypeMetadata.binaryStringRelational,
    [EXPRESSION_TYPES.StringConstant]: expressionTypeMetadata.leafString,
    [EXPRESSION_TYPES.StringDataFieldReference]: expressionTypeMetadata.leafString,
    [EXPRESSION_TYPES.ConditionalString]: expressionTypeMetadata.conditionalString,
    [EXPRESSION_TYPES.ConditionalDate]: expressionTypeMetadata.conditionalDate,
    [EXPRESSION_TYPES.ChoiceEqual]: expressionTypeMetadata.leafChoiceComparison,
    [EXPRESSION_TYPES.ChoiceNotEqual]: expressionTypeMetadata.leafChoiceComparison,
    [EXPRESSION_TYPES.Min]: expressionTypeMetadata.binaryNumeric,
    [EXPRESSION_TYPES.Max]: expressionTypeMetadata.binaryNumeric,
    [EXPRESSION_TYPES.DateConstant]: expressionTypeMetadata.leafDate,
    [EXPRESSION_TYPES.DateDataFieldReference]: expressionTypeMetadata.leafDate,
    [EXPRESSION_TYPES.DateRelational]: expressionTypeMetadata.binaryDate,
    [EXPRESSION_TYPES.DateArithmetic]: expressionTypeMetadata.binaryNumericDate,
    [EXPRESSION_TYPES.Exponent]: expressionTypeMetadata.variableNumeric({ numOperands: 2 }),
    [EXPRESSION_TYPES.Logarithm]: expressionTypeMetadata.unaryNumeric,
    [EXPRESSION_TYPES.StringLength]: expressionTypeMetadata.unaryStringNumeric,
    [EXPRESSION_TYPES.FormulaReference]: expressionTypeMetadata.leafFormulaRef,
    [EXPRESSION_TYPES.ConcatenateStrings]: expressionTypeMetadata.binaryString,
    [EXPRESSION_TYPES.ProjectNumberReference]: expressionTypeMetadata.leafString,
    [EXPRESSION_TYPES.DataFieldReferenceAsString]: expressionTypeMetadata.leafString,
    [EXPRESSION_TYPES.ExistsSameMultiInstanceValue]: expressionTypeMetadata.leafMultiInstanceBoolean,
    [EXPRESSION_TYPES.CoalesceMultiInstanceValue]: expressionTypeMetadata.leafMultiInstanceDynamic,
    [EXPRESSION_TYPES.SumMultiInstanceValue]: expressionTypeMetadata.leafMultiInstanceNumeric,
    [EXPRESSION_TYPES.CountTrueMultiInstanceValue]: expressionTypeMetadata.leafMultiInstanceNumeric,
    [EXPRESSION_TYPES.CountMatchingTextMultiInstanceValue]: expressionTypeMetadata.leafMultiInstanceNumeric,
    [EXPRESSION_TYPES.Round]: expressionTypeMetadata.unaryNumeric,
    [EXPRESSION_TYPES.IsEmpty]: expressionTypeMetadata.leafBoolean,
    [EXPRESSION_TYPES.EnvelopeCompleted]: expressionTypeMetadata.leafBoolean,
    [EXPRESSION_TYPES.DateDifference]: expressionTypeMetadata.binaryDateNumeric
};

export function getClassSuffix(dataFieldType) {
    switch (dataFieldType) {
        case DATA_FIELD_TYPE.Decimal:
            return "numeric";
        case DATA_FIELD_TYPE.Boolean:
            return "boolean";
        case DATA_FIELD_TYPE.String:
            return "string";
        case DATA_FIELD_TYPE.DateTime:
            return "date";
        default:
            return "";
    }
}

export const FORMULA_RESULT_TYPES = {
    Empty: "",
    Numeric: "Numeric",
    Boolean: "Boolean",
    String: "String",
    Date: "Date"
};

export const EXPRESSION_PROPERTY_NAMES = {
    MetaNumberOfOperands: "_meta_numoperands", //number of operands in the dynamic expression
    MetaResultType: "_meta_resulttype", //result type of the dynamic expression ex used in formula expression
    LogBase: "logbase",
    StrictCompare: "strictcompare", //boolean
    DateArithmeticTimeUnits: "timeunits",
    DateBeforeAfter: "beforeafter",
    MultiInstanceFieldGroupType: "multiInstanceFieldGroupType", //should be ESS , PVS, Table
    ESSSubsystemType: "essSubsystemType" /* should be IEB or IB */,
    RoundingAccuracy: "roundingAccuracy",
    IgnoreCase: "ignoreCase",
    DateRangeInclusive: "daterangeinclusive" // boolean
};

export const TIMEUNIT_OPTIONS = [
    { text: "calendar days", value: "calendar" },
    { text: "business days", value: "business" },
    { text: "months", value: "months" },
    { text: "years", value: "years" }
];

export const BEFOREAFTER_OPTIONS = [
    { text: "before", value: "before" },
    { text: "after", value: "after" }
];

export const LOGBASE_OPTIONS = {
    euler: "euler",
    ten: "ten"
};
